// src/components/layout/AppLayout.tsx
import { PropsWithChildren, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  CssBaseline,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import DashboardIcon from "@mui/icons-material/Dashboard";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import RepeatIcon from "@mui/icons-material/Repeat";
import PeopleIcon from "@mui/icons-material/People";
import BusinessIcon from "@mui/icons-material/Business";
import PlaceIcon from "@mui/icons-material/Place";
import InsightsIcon from "@mui/icons-material/Insights";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";

import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "../../app/rootReducer";
import { logout } from "../../features/auth/authSlice";
import Logo from "../common/Logo";

/**
 * Assumptions:
 * - auth slice stores: user, permissions[], activeAgency
 * - logout() clears redux-persist + tokens
 *
 * If your store shape differs, adjust selectors.
 */

const DRAWER_WIDTH = 280;

type NavItem = {
  key: string;
  label: string;
  path: string;
  icon: React.ReactNode;
  anyOfPerms?: string[];
};

function hasAnyPermission(userPerms: string[], anyOf?: string[]) {
  if (!anyOf || anyOf.length === 0) return true;
  return anyOf.some((p) => userPerms.includes(p));
}

function initials(first?: string, last?: string) {
  const a = (first || "").trim().slice(0, 1).toUpperCase();
  const b = (last || "").trim().slice(0, 1).toUpperCase();
  return (a + b) || "U";
}

export default function AppLayout(_props: PropsWithChildren) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileOpen, setMobileOpen] = useState(false);

  const location = useLocation();
  const nav = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector((s: RootState) => s.auth.user);
  const permissions = useSelector((s: RootState) => s.auth.permissions || []);
  const activeAgency = useSelector((s: RootState) => s.auth.activeAgency);

  const navItems: NavItem[] = useMemo(
    () => [
      {
        key: "dashboard",
        label: "Dashboard",
        path: "/app/dashboard",
        icon: <DashboardIcon fontSize="small" />,
        anyOfPerms: ["transfer.read", "masterdata.read", "analytics.read", "transfer.series"],
      },
      {
        key: "transfers",
        label: "Transfers",
        path: "/app/transfers",
        icon: <SyncAltIcon fontSize="small" />,
        anyOfPerms: ["transfer.read", "transfer.write"],
      },
      {
        key: "series",
        label: "Series",
        path: "/app/series",
        icon: <RepeatIcon fontSize="small" />,
        anyOfPerms: ["transfer.series"],
      },
      {
        key: "clients",
        label: "Clients",
        path: "/app/clients",
        icon: <PeopleIcon fontSize="small" />,
        anyOfPerms: ["masterdata.read", "masterdata.write"],
      },
      {
        key: "providers",
        label: "Providers",
        path: "/app/providers",
        icon: <BusinessIcon fontSize="small" />,
        anyOfPerms: ["masterdata.read", "masterdata.write"],
      },
      {
        key: "locations",
        label: "Locations",
        path: "/app/locations",
        icon: <PlaceIcon fontSize="small" />,
        anyOfPerms: ["masterdata.read", "masterdata.write"],
      },
      {
        key: "analytics",
        label: "Analytics",
        path: "/app/analytics",
        icon: <InsightsIcon fontSize="small" />,
        anyOfPerms: ["analytics.read"],
      },
    ],
    []
  );

  const visibleNavItems = useMemo(
    () => navItems.filter((x) => hasAnyPermission(permissions, x.anyOfPerms)),
    [navItems, permissions]
  );

  const selectedKey = useMemo(() => {
    const p = location.pathname;
    // pick best match based on prefix
    const match = visibleNavItems
      .slice()
      .sort((a, b) => b.path.length - a.path.length)
      .find((x) => p === x.path || p.startsWith(x.path + "/"));
    return match?.key || "dashboard";
  }, [location.pathname, visibleNavItems]);

  // User menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const onLogout = () => {
    dispatch(logout());
    nav("/login", { replace: true });
  };

  const DrawerContent = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Logo variant="default" height={50} ariaLabel="KP Golden Tours" />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
              {activeAgency?.name ? activeAgency.name : "KP Golden Tours"}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {activeAgency?.name ? "Agency workspace" : "Workspace"}
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Divider />

      <List sx={{ px: 1, py: 1 }}>
        {visibleNavItems.map((item) => {
          const selected = item.key === selectedKey;
          return (
            <ListItemButton
              key={item.key}
              selected={selected}
              onClick={() => {
                nav(item.path);
                if (!isMdUp) setMobileOpen(false);
              }}
              sx={{
                borderRadius: 2,
                mb: 0.5,
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ fontWeight: selected ? 700 : 500 }}
              />
            </ListItemButton>
          );
        })}
      </List>

      <Box sx={{ flex: 1 }} />

      <Divider />
      <Box sx={{ p: 1.5 }}>
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ width: 36, height: 36 }}>
              {initials(user?.first_name, user?.last_name)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                {(user?.first_name || "") + " " + (user?.last_name || "") || "User"}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {user?.email || ""}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      <CssBaseline />

      {/* Sidebar */}
      {isMdUp ? (
        <Box
          component="nav"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            borderRight: `1px solid ${theme.palette.divider}`,
            borderLeft: `3px solid ${theme.palette.primary.main}`,
            bgcolor: "background.paper",
          }}
        >
          <Box sx={{ width: DRAWER_WIDTH, height: "100vh", position: "sticky", top: 0 }}>
            {DrawerContent}
          </Box>
        </Box>
      ) : (
        // Simple mobile drawer using Menu anchored to AppBar button (lighter than MUI Drawer)
        <Menu
          anchorEl={mobileOpen ? (document.getElementById("mobile-nav-anchor") as any) : null}
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          PaperProps={{ sx: { width: DRAWER_WIDTH, maxWidth: "90vw", p: 0 } }}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <Box sx={{ width: DRAWER_WIDTH }}>{DrawerContent}</Box>
        </Menu>
      )}

      {/* Main */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <AppBar
          position="sticky"
          elevation={0}
          color="default"
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: "background.paper",
          }}
        >
          <Toolbar sx={{ gap: 1.5 }}>
            {!isMdUp ? (
              <IconButton
                id="mobile-nav-anchor"
                edge="start"
                onClick={() => setMobileOpen(true)}
              >
                <MenuIcon />
              </IconButton>
            ) : null}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" color="text.secondary" noWrap>
                {activeAgency?.name ? activeAgency.name : "Workspace"}
              </Typography>
              <Typography variant="h6" sx={{ lineHeight: 1.1 }} noWrap>
                {selectedKey.charAt(0).toUpperCase() + selectedKey.slice(1)}
              </Typography>
            </Box>

            <Tooltip title="Account">
              <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                <Badge color="primary" variant="dot" invisible>
                  <Avatar sx={{ width: 34, height: 34 }}>
                    {initials(user?.first_name, user?.last_name)}
                  </Avatar>
                </Badge>
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem disabled>
                <ListItemIcon>
                  <AccountCircleIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={(user?.first_name || "") + " " + (user?.last_name || "")}
                  secondary={user?.email}
                />
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  onLogout();
                }}
              >
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* Content container */}
        <Box
          sx={{
            flex: 1,
            p: { xs: 1.5, sm: 2, md: 3 },
            maxWidth: 1300,
            width: "100%",
            mx: "auto",
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
